import { useCallback } from "react"
import { graphql } from "relay-runtime"
import { useCustomMutation } from "@/hooks/useCustomMutation"
import { GraphqlErrors } from "@/lib/utils"
import {
  CreateAppointmentInput,
  useCreateAppointmentMutation as useCreateAppointmentMutationType,
  useCreateAppointmentMutation$data,
} from "@/modules/patients/services/__generated__/useCreateAppointmentMutation.graphql"

const mutation = graphql`
  mutation useCreateAppointmentMutation($input: CreateAppointmentInput!, $addConnections: [ID!]!) {
    createAppointment(input: $input) {
      errors {
        ... on Error {
          __typename
          message
        }
      }
      appointment @appendNode(connections: $addConnections, edgeTypeName: "AppointmentEdge") {
        id
        ...PatientAppointmentFragment @relay(mask: false)
        initialBookedDateTime
        initialAssignedToUser {
          id
          firstName
          lastName
        }
        rescheduledBy
        rescheduledAt
        rescheduledByUser {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export function useCreateAppointmentMutation() {
  const [commit, inFlight] = useCustomMutation<useCreateAppointmentMutationType>(mutation)

  const handler = useCallback(
    (
      input: CreateAppointmentInput,
      addConnections: string[],
      onCompleted?: (appointmentId: string) => void,
      onMutationResultError?: (errors: GraphqlErrors) => void,
    ) => {
      return new Promise<NonNullable<useCreateAppointmentMutation$data["createAppointment"]["appointment"]>>(
        (resolve, reject) => {
          commit({
            variables: {
              input,
              addConnections,
            },
            onCompleted(response) {
              if (response.createAppointment.appointment) {
                onCompleted?.(response.createAppointment.appointment.id)
                resolve(response.createAppointment!.appointment!)
              }
            },
            onError(error) {
              reject(error)
            },
            onMutationResultError(errors: GraphqlErrors) {
              onMutationResultError?.(errors)
              reject(errors)
            },
            updater: (store, response) => {
              const data = response?.createAppointment

              if (input.followUpForProgressNoteId && data?.appointment) {
                const appointmentProxy = store.get(data.appointment.id)
                const progressNoteProxy = store.get(input.followUpForProgressNoteId)

                progressNoteProxy?.setLinkedRecord(appointmentProxy!, "followUpAppointment")
              }
            },
          })
        },
      )
    },
    [commit],
  )

  return [handler, inFlight] as const
}
